import { useCallback, useEffect, useState } from "react";

import LoadingButton from "@mui/lab/LoadingButton";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import useAccount from "@hooks/useAccount";
import useAllowance from "@hooks/useAllowance";
import useApprove from "@hooks/useApprove";
import useContract from "@hooks/useContract";
import useToast from "@hooks/useToast";
import useTokenBalance from "@hooks/useTokenBalance";

import type { MyLocale } from "@i18n";

import NodeController05Abi from "@abi/NodeController05";
import AlertBox from "@components/AlertBox";
import config from "@config";
import { formatNumber } from "@utils/formatRewards";
import { useI18n } from "next-rosetta";
import { type Address, maxUint256, parseEther } from "viem";
import { useSimulateContract, useWriteContract } from "wagmi";

const useCreateNode = (nodeName: string, amount: string) => {
	const { nodeController05Contract } = useContract();
	const { data } = useSimulateContract({
		abi: NodeController05Abi,
		address: nodeController05Contract.address as Address,
		args: [nodeName, typeof amount === "bigint" ? amount : BigInt(amount)],
		functionName: "createNode",

		gas: BigInt("500000"),
	});

	const {
		isPending: isLoading,
		reset,
		status,
		writeContract,
	} = useWriteContract();
	const state = status;

	const write = useCallback(() => {
		if (data?.request) {
			return writeContract(data?.request);
		}
	}, [data]);
	return {
		isLoading,
		resetState: reset,
		send: write,
		state,
		status,
	};
};

export default function CreateNode({ open, setOpen }) {
	const i18n = useI18n<MyLocale>();
	const { t } = i18n;
	const { tokenContract } = useContract();
	const [nodeName, setNodeName] = useState("");
	const [nodeAmount, setNodeAmount] = useState(config.appTokenomics.minPrice);
	// const [referral, setReferral] = useState("");
	const { account } = useAccount();
	const {
		isLoading,
		send: createNode,
		state: createNodeState,
		status,
	} = useCreateNode(nodeName, parseEther(nodeAmount?.toString())?.toString());
	const spender = config.web3.vaporControllerAddress as Address;

	const approveAmt = maxUint256;
	const { isLoading: isApproveLoading, send: approve } = useApprove(
		tokenContract?.address as Address,
		spender,
		approveAmt,
	);
	const allowance = useAllowance(account, spender);
	const {
		tokenBalance,
		tokenBalanceFormatted,
		tokenBalanceFormattedShortened,
	} = useTokenBalance(account);
	const { contractFunctionToast } = useToast();

	const isLowBalance = tokenBalance
		? Number(tokenBalanceFormatted) < config.appTokenomics.minPrice
		: true;
	const isApproved = allowance !== "0";
	const notEnoughTokens =
		+tokenBalanceFormattedShortened < Number(config.appTokenomics.minPrice);
	const isDisabled = !nodeName || !nodeAmount || !isApproved || notEnoughTokens;
	const shouldDisableContinueButton = isDisabled || isLoading;

	const handleClose = () => {
		setOpen(false);
	};

	const handleNameChange = (event) => {
		setNodeName(event.target.value);
	};

	const handleAmountChange = (event) => {
		setNodeAmount(event.target.value);
	};

	const handleCreateNode = () => {
		createNode();
	};

	const handleApprove = () => {
		approve();
	};

	// const handleReferralChange = (event) => {
	//   setReferral(event.target.value);
	// };

	const handleOnClickMaxAmount = () => {
		setNodeAmount(Number(tokenBalanceFormatted));
	};

	useEffect(() => {
		if (status && status === "success") {
			window?.setTimeout(() => {
				location.reload();
			}, 2000);
		}
	}, [status]);

	useEffect(() => {
		contractFunctionToast("Create", createNodeState);
	}, [createNodeState, contractFunctionToast]);

	// const referralCookie = Cookies.get("referral");
	// useEffect(() => {
	//   if (referralCookie) {
	//     setReferral(referralCookie);
	//   }
	// }, [referralCookie]);

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			PaperProps={{
				sx: {
					backgroundImage: "unset",
					borderRadius: 5,
				},
			}}
		>
			<DialogTitle>{t("modals.createNode.join")}</DialogTitle>

			<DialogContent>
				{isLowBalance && (
					<AlertBox>
						{t("modals.createNode.notEnough", {
							minPrice: formatNumber(config.appTokenomics.minPrice),
						})}{" "}
						{t("modals.createNode.youCan")}{" "}
						<Link
							href={`https://traderjoexyz.com/trade?outputCurrency=${config.web3.vpndAddress}#/`}
							target="_blank"
						>
							{t("modals.createNode.buy")}
						</Link>
						.
					</AlertBox>
				)}

				<FormControl fullWidth sx={{ marginBottom: 2, marginTop: 2 }}>
					<TextField
						autoFocus
						margin="dense"
						id="name"
						label={t("modals.createNode.nodeName")}
						type="text"
						fullWidth
						variant="standard"
						value={nodeName}
						onChange={handleNameChange}
						error={
							nodeName.length > 0 &&
							(nodeName.length <= 3 || nodeName.length >= 32)
						}
						required
					/>
				</FormControl>

				<FormControl fullWidth>
					<TextField
						margin="dense"
						id="amount"
						label={t("modals.createNode.nodeAmount")}
						type="number"
						fullWidth
						variant="standard"
						helperText={t("modals.createNode.availableNodes", {
							quantity: tokenBalanceFormattedShortened || 0,
						})}
						value={nodeAmount}
						onChange={handleAmountChange}
						required
						InputProps={{
							endAdornment: (
								<Button
									variant="text"
									onClick={handleOnClickMaxAmount}
									sx={{ boxShadow: "unset" }}
								>
									Max
								</Button>
							),
						}}
					/>
				</FormControl>

				<DialogContentText component="div">
					<br />
					{t("modals.createNode.content")}{" "}
					<Link href="https://docs.vapornodes.finance" target="_blank">
						{t("modals.createNode.documentation")}
					</Link>
					<br />
					<br />
					<Typography variant="caption">
						** {t("modals.createNode.distribution")}
					</Typography>
				</DialogContentText>
			</DialogContent>

			<DialogActions
				sx={{
					justifyContent: "space-between",
					paddingBottom: 4,
					px: 4,
				}}
			>
				<Button variant="outlined" color="error" onClick={handleClose}>
					{t("modals.createNode.cancel")}
				</Button>

				<Tooltip
					title={`You need at least ${formatNumber(
						config.appTokenomics.minPrice,
					)} VPND to create a node`}
				>
					<Stack direction="row" spacing={2}>
						{isApproved ? (
							<LoadingButton
								variant="contained"
								color="primary"
								onClick={handleCreateNode}
								disabled={shouldDisableContinueButton}
								loading={isLoading}
							>
								{notEnoughTokens
									? t("modals.createNode.notEnoughTokens", { tokens: "VPND" })
									: t("modals.createNode.continue")}
							</LoadingButton>
						) : (
							<LoadingButton
								variant="contained"
								onClick={handleApprove}
								disabled={isApproved}
								loading={isApproveLoading}
							>
								{t("modals.createNode.approve")}
							</LoadingButton>
						)}
					</Stack>
				</Tooltip>
			</DialogActions>
		</Dialog>
	);
}
